import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";

import BackgroundComponent from "../../components/Layout/BackgroundProfile/BackgroundProfile";
import ProfileViewModeBg from "../../assets/images&icons/profileViewMode.jpeg";
import SocialLinks from "../../components/ViewMode/SocialLinks";
import { getOfficesByFirmId } from "../../Services/Firm";
import { IPerson } from "../../interfaces";
import Disclosure from "../../components/Moleculas/Disclosure/Disclosure";
import { IAreaOfPractice } from "../../interfaces/models/IAreaOfPractice";
import { IOfficeNew } from "../../interfaces/models/IOffice";
import { Appstore } from "../../redux";
import Professionals from "./Profile/Professionals";
import OverviewCard from "./Profile/OverviewCard";
import NewButton from "../../components/Atomos/Buttons/newButton";
import ArticleCard from "./Profile/ArticleCard";
import ContactFrame from "./Profile/ContactFrame";
import AopFirm from "./Profile/AopFirm";
import useViewport from "../../hooks/useViewPort";
import { getAOPsByFirm } from "../../Services/AreaOfPractice";
import { fetchPersonByFirm } from "../../Services/Person";
import { getOfficeContactByFirmId } from "../../Services/MemberMainContact";
import { IAddress } from "../../interfaces/models/IAddress";
import { getContentsByFirmAndType } from "../../Services/Content";
import { getAddresById } from "../../Services/Address";
import { getPracticeIndustrybyIds } from "../../Services/PracticeIndustry";
import { getJurisdictionLabel } from "../../utils/jurisdiction/structuredJurisdiction";

const WSGDots = require("../../assets/images&icons/WSGDots.svg");

/** API may still return legacy office fields not on IOfficeNew. */
type OfficeRecord = IOfficeNew & {
  name?: string;
  addressName?: string;
};

function isMainOffice(office: OfficeRecord): boolean {
  return String(office.officeType ?? "").toLowerCase() === "main";
}

function officeDisplayName(office: OfficeRecord): string {
  const legacyName = String(office.name ?? "").trim();
  if (legacyName) {
    return legacyName;
  }

  return (
    getJurisdictionLabel(office) ||
    [office.stateName, office.cityName].filter(Boolean).join(", ") ||
    String(office.cityName ?? "").trim() ||
    String(office.id ?? "").trim()
  );
}

const ManageFirmProfile = () => {
  const manageFirm = useSelector((state: Appstore) => state.manageFirm);
  const { viewportWidth } = useViewport();
  const representativeClients =
    manageFirm.selectedFirm?.representativeClients?.split("\r");
  const [personList, setPersonList] = useState([]);
  const currentFirm = manageFirm.selectedFirm;
  const [office, setOffice] = useState<IOfficeNew>();
  const [offices, setOffices] = useState<IOfficeNew[]>([]);
  const [mainProfessionalsByOffice, setMainProfessionalsByOffice] = useState<
    IPerson[]
  >([]);
  const [areasOfPractice, setAreasOfPractice] = useState<IAreaOfPractice[]>([]);
  const [addressInfo, setAddressInfo] = useState<IAddress>();
  const [practiceIndustries, setPracticeIndustries] = useState([]);
  const [articles, setArticles] = useState([]);
  const [presses, setPresses] = useState([]);
  const [guides, setGuides] = useState([]);
  const [cardsOpen, setCardsOpen] = useState({
    overview: true,
    aop: true,
    repClients: true,
    professionals: true,
    articles: true,
    press: true,
    blog: true,
    guides: true,
  });

  const getArticles = async (firmId: string) => {
    try {
      const response = await getContentsByFirmAndType(firmId, "article", 3);
      setArticles(response);
    } catch (error) {
      console.error(error);
    }
  };

  const getPresses = async (firmId: string) => {
    try {
      const response = await getContentsByFirmAndType(firmId, "press", 3);
      setPresses(response);
    } catch (error) {
      console.error(error);
    }
  };

  const getGuides = async (firmId: string) => {
    try {
      const response = await getContentsByFirmAndType(firmId, "guide", 3);
      setGuides(response);
    } catch (error) {
      console.error(error);
    }
  };

  const getAOPs = async (firmId: string) => {
    try {
      const response = await getAOPsByFirm(firmId);
      setAreasOfPractice(response);
    } catch (error) {
      console.error(error);
    }
  };

  const getOfficesInfo = async (id: string) => {
    const officesList = await getOfficesByFirmId(id);
    const mainOffice =
      officesList.find(isMainOffice) ?? officesList[0] ?? undefined;
    setOffices(officesList);
    setOffice(mainOffice);
  };

  const getPracticeIndustriesByAops = async (aopIds: string[]) => {
    try {
      const practiceIndustries = await getPracticeIndustrybyIds(aopIds);
      setPracticeIndustries(practiceIndustries);
    } catch (error) {
      console.error(error);
    }
  };

  const getAllPersons = async (firmId: string) => {
    try {
      const persons = await fetchPersonByFirm(firmId);
      setPersonList(persons);
    } catch (error) {
      console.error(error);
    }
  };

  const getProfessionalsByOffice = async (officeId: string) => {
    if (!currentFirm?.id) {
      return;
    }
    const officeMainContactsByFirm = await getOfficeContactByFirmId(
      currentFirm.id,
    );
    if (!officeMainContactsByFirm?.offices?.length) {
      return;
    }

    const mainContacts = officeMainContactsByFirm?.offices?.find(
      (o: any) => o.office === officeId,
    )?.contacts;
    if (!mainContacts?.length) {
      return;
    }
    const matchedPersons = personList.filter((person: IPerson) =>
      mainContacts?.map((m: any) => m.contact).includes(person.id),
    );
    setMainProfessionalsByOffice(matchedPersons);
  };

  const getAddress = async (addressId: string) => {
    try {
      const response = await getAddresById(addressId);
      setAddressInfo(response);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchData = async () => {
    if (!currentFirm?.id) {
      return;
    }

    try {
      await Promise.all([
        getOfficesInfo(currentFirm.id),
        getArticles(currentFirm.id),
        getPresses(currentFirm.id),
        getGuides(currentFirm.id),
        getAllPersons(currentFirm.id),
        getAOPs(currentFirm.id),
      ]);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (office?.id) {
      getProfessionalsByOffice(office.id);
    }
    if (office?.address) {
      getAddress(office.address);
    }
  }, [office]);

  useEffect(() => {
    if (!areasOfPractice?.length) {
      return;
    }
    getPracticeIndustriesByAops(areasOfPractice?.map((a) => a.id));
  }, [areasOfPractice]);

  const handleOfficeChange = (selectedOffice: OfficeRecord) => {
    setOffice(selectedOffice);
    if (currentFirm?.id) {
      getAOPs(currentFirm.id);
    }
  };

  return (
    <>
      <BackgroundComponent image={ProfileViewModeBg} />
      <div className="bg-gray-100 min-h-screen text-left px-4 py-7 items-start flex flex-col gap-[1.5rem] md:py-8 md:px-6 lg:py-10 lg:px-10 lg:gap-[2.5rem]">
        <div className="w-full relative z-10 flex gap-5 md:gap-6 lg:gap-7 !items-end self-stretch !p-0 flex-col ">
          <div className="flex p-0 flex-col justify-center items-end gap-5 self-stretch md:flex-row lg:justify-between lg:gap-6">
            <h2 className="text-left w-full md:w-max text-white font-decimal text-[2.25rem] font-medium leading-[2.75rem] md:text-[2.75rem] md:leading-[3.25rem] lg:text-[3.25rem] lg:leading-[4.5rem]">{`${currentFirm?.name} Preview`}</h2>
            <div
              className="self-stretch flex-1 hidden md:flex"
              style={{
                backgroundImage: `url(${WSGDots.default})`,
              }}
            ></div>
            <div className="w-full md:w-fit">
              <Link
                to={`/ManageFirm/Profile/modify/${currentFirm?.id}`}
                state={{
                  offices: offices,
                  mainContacts: mainProfessionalsByOffice,
                  aops: areasOfPractice,
                }}
              >
                <NewButton
                  text={`MODIFY`}
                  color="accent"
                  type="button"
                  style="filled"
                  size="large"
                  content="text"
                  className="!h-[60px] lg:!h-[72px]"
                />
              </Link>
            </div>
          </div>
          <hr className="flex h-0 justify-center items-center self-stretch relative z-10" />
          <div className="flex w-full"></div>
        </div>

        <div
          className="flex py-5 px-4 flex-col items-start gap-5 self-stretch bg-white relative
          md:py-7 md:px-7 md:gap-6
          lg:py-11 lg:px-12 lg:gap-10"
        >
          <div
            className="flex p-0 flex-col items-start gap-7 self-stretch 
            lg:gap-[1.25rem]"
          >
            <hr className="flex h-0 lg:justify-center lg:items-center self-stretch"></hr>
          </div>
          <div className="flex flex-col items-center gap-7 lg:gap-10 self-stretch">
            <div className="flex flex-col items-center gap-7 lg:gap-[1.125rem] self-stretch">
              <div className="flex flex-col items-center lg:items-start md:flex-col lg:justify-center gap-[1.125rem] self-stretch flex-1">
                <div className="flex flex-col md:flex-row md:justify-center md:items-start lg:items-center items-center gap-[1.5rem] md:gap-[.625rem] md:flex-1 self-stretch">
                  <div className="flex gap-[1.25rem] md:w-[21.5rem] lg:w-[39.625rem] flex-col lg:justify-center items-center lg:gap-[.625rem] lg:flex-1">
                    <div className="flex flex-col justify-center items-start gap-[.625rem] self-stretch md:flex-1 w-full">
                      <span className="self-stretch text-primary font-decimal text-[2.375rem] leading-[2.75rems]">
                        {currentFirm?.firmName}
                      </span>
                      <span className="self-stretch text-center md:text-left text-gray-font lg:text-lg lg:leading-5">
                        {currentFirm?.serviceType}
                      </span>
                      <div className="flex justify-center md:justify-start items-center gap-2 self-stretch">
                        <SocialLinks
                          organization
                          organizationObj={currentFirm ? currentFirm : []}
                        />
                      </div>
                      <Link
                        to={currentFirm?.websiteUrl ?? ""}
                        className="self-stretch text-center md:text-left text-Default lg:text-lg lg:leading-5 underline"
                      >
                        {currentFirm?.websiteUrl}
                      </Link>
                    </div>
                  </div>
                  <div className="flex flex-1 justify-center align-middle mb-5 self-stretch ">
                    {currentFirm?.logo && (
                      <img
                        src={`${currentFirm?.logo ?? ""}`}
                        alt="Wsg_Profile_Picture"
                        className="object-contain max-h-[174px] max-w-[330px]"
                      />
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-start gap-[.625rem] self-stretch">
                  <div className="flex flex-col items-start gap-4 self-stretch">
                    <span className="text-gray-font text-[1.125rem] leading-normal lg:text-[1.375rem] font-bold lg:leading-6">
                      Office Selected: {office?.firmName}
                    </span>
                    <div className="flex flex-col justify-center items-start gap-3 self-stretch  overflow-x-auto relative overflow-y-hidden noscrollbar">
                      <div className="flex lg:items-center lg:gap-4">
                        <span className="text-[.875rem] leading-5 text-gray-font lg:text-lg">
                          {office?.phoneNumber?.code || ""}{" "}
                          {office?.phoneNumber?.phone || ""}
                        </span>
                        <span className="text-[.875rem] leading-5 text-gray-font lg:text-lg">
                          {office?.faxNumber?.code || ""}{" "}
                          {office?.faxNumber?.phone || ""}
                        </span>
                      </div>
                      <div className="flex gap-4 items-center">
                        <span className="whitespace-nowrap text-[.875rem] leading-5 text-gray-font lg:text-lg">
                          {office?.address}
                        </span>
                        <hr className="w-0 h-[1.25rem]" />
                        <span className="text-[.875rem] leading-5 text-gray-font lg:text-lg whitespace-nowrap">
                          {office?.cityName}
                        </span>
                        <hr className="w-0 h-[1.25rem]" />
                        <span className="text-[.875rem] leading-5 text-gray-font lg:text-lg whitespace-nowrap">
                          {office?.stateName}
                        </span>
                        <hr className="w-0 h-[1.25rem]" />
                        <span className="text-[.875rem] leading-5 text-gray-font lg:text-lg">
                          {addressInfo?.zipCode}
                        </span>
                        <hr className="w-0 h-[1.25rem]" />
                        <span className="text-[.875rem] leading-5 text-gray-font lg:text-lg">
                          {office?.countryName}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {!!mainProfessionalsByOffice?.length && (
                <>
                  <hr className="flex h-0 lg:justify-center lg:items-center self-stretch"></hr>
                  <div className="flex flex-col items-center gap-4 self-stretch">
                    <span className="text-gray-font text-[1.125rem] leading-normal lg:text-[1.375rem] font-bold lg:leading-6 self-stretch">
                      {office?.firmName} Main Contacts
                    </span>
                    <div
                      className={`flex flex-col md:grid md:grid-cols-2 ${viewportWidth < 1250 ? "lg:grid-cols-3" : "lg:grid-cols-4"} lg:flex-wrap items-center gap-10 self-stretch`}
                    >
                      {mainProfessionalsByOffice.map(
                        (professional: IPerson) => (
                          <React.Fragment key={professional.id}>
                            <ContactFrame person={professional} />
                          </React.Fragment>
                        ),
                      )}
                    </div>
                  </div>
                </>
              )}
              {offices && offices.length > 0 && (
                <React.Fragment>
                  <hr className="flex h-0 lg:justify-center lg:items-center self-stretch"></hr>
                  <div className="flex flex-col items-center gap-4 self-stretch">
                    <span className="text-gray-font text-[1.125rem] leading-normal lg:text-[1.375rem] font-bold lg:leading-6 self-stretch">
                      Offices
                    </span>
                    <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-8 lg:items-start justify-between gap-4 lg:flex-1 lg:flex-wrap self-stretch">
                      {offices.map((currentOffice) => (
                        <div
                          key={currentOffice.id}
                          className={`grid col-span-1 lg:flex-col lg:items-start flex-wrap`}
                        >
                          <span
                            className={`text-Default text-base leading-[1.125rem] lg:text-[1.125rem] lg:leading-5 cursor-pointer ${currentOffice.id === office?.id ? "cursor-default" : ""}`}
                            onClick={() => handleOfficeChange(currentOffice)}
                          >
                            {officeDisplayName(currentOffice)}
                          </span>
                          <hr className="h-0 w-[.625rem]" />
                        </div>
                      ))}
                      <hr className="w-0 h-[2.4375rem]" />
                    </div>
                  </div>
                </React.Fragment>
              )}
            </div>
          </div>
        </div>
        <Disclosure
          title="Overview"
          isCardOpen={cardsOpen.overview}
          onCardOpen={() =>
            setCardsOpen((prev) => ({ ...prev, overview: !prev.overview }))
          }
          color="secondary"
        >
          <OverviewCard
            overview={office?.overview || currentFirm?.firmOverview}
          />
        </Disclosure>
        <Disclosure
          title="Areas Of Practice"
          isCardOpen={cardsOpen.aop}
          onCardOpen={() =>
            setCardsOpen((prev) => ({ ...prev, aop: !prev.aop }))
          }
          color="secondary"
        >
          <AopFirm
            practiceIndustries={practiceIndustries}
            aops={areasOfPractice}
          />
        </Disclosure>
        <Disclosure
          title="Representative Clients"
          isCardOpen={cardsOpen.repClients}
          onCardOpen={() =>
            setCardsOpen((prev) => ({ ...prev, repClients: !prev.repClients }))
          }
          color="secondary"
        >
          {!!representativeClients && (
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-5 self-stretch">
              {representativeClients?.map((representativeClient: string) => (
                <div key={representativeClient}>
                  <span>{representativeClient}</span>
                </div>
              ))}
            </div>
          )}
        </Disclosure>
        <Disclosure
          title="Professionals"
          isCardOpen={cardsOpen.professionals}
          onCardOpen={() =>
            setCardsOpen((prev) => ({
              ...prev,
              professionals: !prev.professionals,
            }))
          }
          color="secondary"
        >
          <Professionals professionals={personList} />
        </Disclosure>

        <Disclosure
          title="Articles"
          isCardOpen={cardsOpen.articles}
          onCardOpen={() =>
            setCardsOpen((prev) => ({ ...prev, articles: !prev.articles }))
          }
          color="secondary"
        >
          <ArticleCard
            articles={articles ?? []}
            type="article"
            viewAllLink={`/ManageFirm/Content/${currentFirm?.id}`}
            viewMoreLink={`/ManageFirm/Profile/${currentFirm?.id}/article`}
          />
        </Disclosure>

        <Disclosure
          title="Press"
          isCardOpen={cardsOpen.press}
          onCardOpen={() =>
            setCardsOpen((prev) => ({ ...prev, press: !prev.press }))
          }
          color="secondary"
        >
          <ArticleCard
            articles={presses ?? []}
            type="press"
            viewAllLink={`/ManageFirm/Content/${currentFirm?.id}`}
            viewMoreLink={`/ManageFirm/Profile/${currentFirm?.id}/press`}
          />
        </Disclosure>

        {/*     RWSG2-122 - Hiding Blog Card for now
        <Disclosure
          title="Blog"
          isCardOpen={cardsOpen.blog}
          onCardOpen={() => setCardsOpen((prev) => ({ ...prev, blog: !prev.blog }))}
          color="secondary"
        >
          <div className="grid grid-cols-6 gap-7 w-full py-7 px-7 lg:py-11 lg:px-12 flex-col items-end lg:gap-[1.75rem] self-stretch">
            {pressCardData.map((item, index) => (
              <React.Fragment key={index}>
                <BlogCard />
                {index < pressCardData.length - 1 && (
                  <div className="grid flex-1 col-span-6">
                    <Vector className="w-full h-0 stroke-gray-50 stroke-1" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </Disclosure>
 */}
        <Disclosure
          title="Guides"
          isCardOpen={cardsOpen.guides}
          onCardOpen={() =>
            setCardsOpen((prev) => ({ ...prev, guides: !prev.guides }))
          }
          color="secondary"
        >
          <ArticleCard
            articles={guides ?? []}
            type="guide"
            viewAllLink={`/ManageFirm/Content/${currentFirm?.id}`}
            viewMoreLink={`/ManageFirm/Profile/${currentFirm?.id}/guide`}
          />
        </Disclosure>
      </div>
    </>
  );
};

export default ManageFirmProfile;
